import { useFormContext } from 'react-hook-form'
import { useTranslations } from '@/hooks/useTranslations'

import { FormSection } from '@/components/common'
import { BillExtraChargesGrid } from '@/components/pages/Admin/BillManagement/Form/BillExtraChargesGrid'
import { BillLineItemsGrid } from '@/components/pages/Admin/BillManagement/Form/BillLineItemsGrid'
import { BillFormExcelOrderSection } from '@/components/pages/Admin/BillManagement/Form/BillFormExcelOrderSection'
import { BillCustomerTextCombobox } from '@/components/pages/Admin/BillManagement/Form/BillCustomerTextCombobox'
import { BillWholesaleCustomerField } from '@/components/pages/Admin/BillManagement/Form/BillWholesaleCustomerField'
import type { BillFormGridVariant, BillFormViewSharedProps } from '@/components/pages/Admin/BillManagement/Form/billFormViewShared'
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui'
import {
  BILL_BUY_PARTY_LABELS,
  BILL_BUY_PARTY_NAME_LABELS,
  BILL_BUY_PARTY_NAME_PLACEHOLDERS,
  BILL_BUY_PARTY_VALUES,
} from '@/constants/bill-buy-party'
import { BILL_LOCATION_LABELS, BILL_LOCATION_VALUES } from '@/constants/bill-location'
import { formatNumber } from '@/utils/number'
import { cn } from '@/lib/utils'

export function BillFormClassicView({
  control,
  isLoading,
  isBuyBill,
  isWholesaleSell,
  billTypeWatch,
  buyPartyForLabels,
  onBillTypeChange,
  lineItemFields,
  appendLineItem,
  removeLineItem,
  lineItems,
  syncWholesaleRowWeight,
  extraChargeFields,
  appendExtraCharge,
  removeExtraCharge,
  extraCharges,
  customerNameSuggestions,
  profileLoading,
  applyCustomerProfile,
  customerNameWatch,
  totalKg,
  subtotalBaht,
  extraChargeTotal,
  discountWatch,
  isWholesaleSellSummary,
  netBaht,
  lineItemsErrorMessage,
  hasLineItemsError,
  gridVariant = 'default',
}: BillFormViewSharedProps & { gridVariant?: BillFormGridVariant }) {
  const t = useTranslations('admin.bill_management.form')
  const { setValue, getValues } = useFormContext()

  return (
    <>
      <FormSection
        title={t('bill_information.title')}
        className="rounded-b-none rounded-t-md border border-border/60 bg-transparent"
        contentClassName="space-y-2 p-2 md:p-2"
      >
        <div className="border border-border/50 bg-transparent p-2">
          <div className="grid min-w-0 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <FormField
              control={control}
              name="bill_date"
              render={({ field }) => (
                <FormItem className="min-w-0">
                  <FormLabel required>วันที่บิล</FormLabel>
                  <FormControl>
                    <Input
                      type="date"
                      className="h-9 w-full border-border/80 bg-background"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="location"
              render={({ field }) => (
                <FormItem className="min-w-0">
                  <FormLabel required>สถานที่</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger className="h-9 w-full border-border/80 bg-background">
                        <SelectValue placeholder="เลือกจังหวัด" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {BILL_LOCATION_VALUES.map((loc) => (
                        <SelectItem key={loc} value={loc}>
                          {BILL_LOCATION_LABELS[loc]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="bill_information.bill_type"
              render={({ field }) => (
                <FormItem className="min-w-0 sm:col-span-2 xl:col-span-1">
                  <FormLabel required>{t('bill_information.bill_type.label')}</FormLabel>
                  <FormControl>
                    <Select
                      value={field.value}
                      onValueChange={(value) => {
                        field.onChange(value)
                        if (value === 'buy') {
                          setValue('bill_information.sale_mode', 'retail')
                          if (!getValues('bill_information.buy_party_type')) {
                            setValue('bill_information.buy_party_type', 'stall')
                          }
                        } else {
                          setValue('bill_information.buy_party_type', undefined)
                        }
                        onBillTypeChange(value)
                      }}
                    >
                      <SelectTrigger className="h-9 w-full border-border/80 bg-background">
                        <SelectValue placeholder={t('bill_information.bill_type.placeholder')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="buy">{t('bill_information.bill_type.buy')}</SelectItem>
                        <SelectItem value="sell">{t('bill_information.bill_type.sell')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {billTypeWatch === 'sell' ? (
              <FormField
                control={control}
                name="bill_information.sale_mode"
                render={({ field }) => (
                  <FormItem className="min-w-0 sm:col-span-2 xl:col-span-4">
                    <FormLabel>{t('bill_information.sale_mode_label')}</FormLabel>
                    <FormControl>
                      <RadioGroup
                        className="grid grid-cols-2 gap-2 pt-1 md:max-w-md"
                        value={field.value ?? 'retail'}
                        onValueChange={field.onChange}
                      >
                        <Label
                          htmlFor="sale_mode_retail"
                          className={cn(
                            'flex h-9 items-center gap-2 rounded-md border border-border/80 bg-background px-3 text-sm font-medium',
                            'cursor-pointer transition-colors hover:bg-accent/60',
                            (field.value ?? 'retail') === 'retail' &&
                              'border-primary bg-primary/10 text-foreground',
                          )}
                        >
                          <RadioGroupItem value="retail" id="sale_mode_retail" />
                          {t('bill_information.sale_mode_retail')}
                        </Label>
                        <Label
                          htmlFor="sale_mode_wholesale"
                          className={cn(
                            'flex h-9 items-center gap-2 rounded-md border border-border/80 bg-background px-3 text-sm font-medium',
                            'cursor-pointer transition-colors hover:bg-accent/60',
                            field.value === 'wholesale' &&
                              'border-primary bg-primary/10 text-foreground',
                          )}
                        >
                          <RadioGroupItem value="wholesale" id="sale_mode_wholesale" />
                          {t('bill_information.sale_mode_wholesale')}
                        </Label>
                      </RadioGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ) : null}
            {isBuyBill ? (
              <FormField
                control={control}
                name="bill_information.buy_party_type"
                render={({ field }) => (
                  <FormItem className="min-w-0 sm:col-span-2 xl:col-span-4">
                    <FormLabel required>{t('bill_information.buy_party_type_label')}</FormLabel>
                    <FormControl>
                      <RadioGroup
                        className="grid grid-cols-2 gap-2 pt-1 md:max-w-md"
                        value={field.value ?? 'stall'}
                        onValueChange={field.onChange}
                      >
                        {BILL_BUY_PARTY_VALUES.map((party) => {
                          const checked = (field.value ?? 'stall') === party
                          return (
                            <Label
                              key={party}
                              htmlFor={`buy_party_${party}`}
                              className={cn(
                                'flex h-9 items-center gap-2 rounded-md border border-border/80 bg-background px-3 text-sm font-medium',
                                'cursor-pointer transition-colors hover:bg-accent/60',
                                checked && 'border-primary bg-primary/10 text-foreground',
                              )}
                            >
                              <RadioGroupItem value={party} id={`buy_party_${party}`} />
                              {BILL_BUY_PARTY_LABELS[party]}
                            </Label>
                          )
                        })}
                      </RadioGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ) : null}
            {isWholesaleSell ? (
              <>
                <FormField
                  control={control}
                  name="bill_information.container_no"
                  render={({ field }) => (
                    <FormItem className="min-w-0 sm:col-span-2 xl:col-span-1">
                      <FormLabel>{t('bill_information.wholesale_container')}</FormLabel>
                      <FormControl>
                        <Input
                          className="h-9 w-full border-border/80 bg-background"
                          placeholder={t('bill_information.wholesale_container_placeholder')}
                          {...field}
                          value={field.value ?? ''}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={control}
                  name="bill_information.customer_sequence_no"
                  render={({ field }) => (
                    <FormItem className="min-w-0 sm:col-span-2 xl:col-span-1">
                      <FormLabel>{t('bill_information.wholesale_cust_no')}</FormLabel>
                      <FormControl>
                        <Input
                          className="h-9 w-full border-border/80 bg-background"
                          placeholder={t('bill_information.wholesale_cust_no_placeholder')}
                          readOnly
                          disabled
                          {...field}
                          value={field.value ?? ''}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={control}
                  name="bill_information.discount_baht"
                  render={({ field }) => (
                    <FormItem className="min-w-0 sm:col-span-2 xl:col-span-2">
                      <FormLabel>{t('bill_information.wholesale_discount')}</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min={0}
                          step="0.01"
                          className="h-9 w-full border-border/80 bg-background tabular-nums"
                          {...field}
                          value={field.value === 0 ? '' : field.value}
                          onChange={(e) => {
                            const v = e.target.value
                            field.onChange(v === '' ? 0 : parseFloat(v))
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="min-w-0 sm:col-span-2 xl:col-span-4">
                  <FormLabel>{t('bill_information.wholesale_extra_charges')}</FormLabel>
                  <BillExtraChargesGrid
                    control={control}
                    fields={extraChargeFields}
                    append={appendExtraCharge}
                    remove={removeExtraCharge}
                    extraCharges={extraCharges}
                    disabled={isLoading}
                    variant={gridVariant}
                  />
                </div>
              </>
            ) : null}
          </div>
        </div>
      </FormSection>

      <FormSection
        title={t('user_information.title')}
        className={cn(
          'border-x border-border/60 bg-transparent',
          gridVariant === 'excel'
            ? 'mb-3 rounded-b-md rounded-t-none border-b'
            : 'rounded-none',
        )}
        contentClassName="space-y-2 p-2 md:p-2"
      >
        <div className="border border-border/50 bg-transparent p-2">
          <div className="grid min-w-0 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <FormField
              control={control}
              name="customer_information.name"
              render={({ field }) => (
                <FormItem className="min-w-0 sm:col-span-2 xl:col-span-2">
                  <FormLabel required>
                    {isBuyBill
                      ? BILL_BUY_PARTY_NAME_LABELS[buyPartyForLabels]
                      : t('user_information.username.label')}
                  </FormLabel>
                  <FormControl>
                    <BillCustomerTextCombobox
                      value={field.value ?? ''}
                      onChange={field.onChange}
                      suggestions={customerNameSuggestions}
                      loading={profileLoading}
                      placeholder={
                        isBuyBill
                          ? BILL_BUY_PARTY_NAME_PLACEHOLDERS[buyPartyForLabels]
                          : t('user_information.username.placeholder')
                      }
                      disabled={isLoading}
                      onSelectOption={(name) => {
                        void applyCustomerProfile(name)
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="customer_information.phone"
              render={({ field }) => (
                <FormItem className="min-w-0">
                  <FormLabel>{t('user_information.phone.label')}</FormLabel>
                  <FormControl>
                    <Input
                      className="h-9 w-full border-border/80 bg-background"
                      placeholder={t('user_information.phone.placeholder')}
                      disabled={isLoading}
                      autoComplete="off"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="customer_information.car_registration"
              render={({ field }) => (
                <FormItem className="min-w-0">
                  <FormLabel>{t('user_information.car_registration.label')}</FormLabel>
                  <FormControl>
                    <Input
                      className="h-9 w-full border-border/80 bg-background"
                      placeholder={t('user_information.car_registration.placeholder')}
                      disabled={isLoading}
                      autoComplete="off"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {isWholesaleSell ? (
              <>
                <FormField
                  control={control}
                  name="customer_information.address"
                  render={({ field }) => (
                    <FormItem className="min-w-0 sm:col-span-2 xl:col-span-4">
                      <FormLabel>{t('user_information.address.label')}</FormLabel>
                      <FormControl>
                        <BillWholesaleCustomerField
                          customerName={customerNameWatch ?? ''}
                          field="address"
                          value={field.value ?? ''}
                          onChange={field.onChange}
                          placeholder={t('user_information.address.placeholder')}
                          disabled={isLoading}
                          requiresCustomerHint={t('user_information.autocomplete_requires_customer')}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={control}
                  name="customer_information.tax_id"
                  render={({ field }) => (
                    <FormItem className="min-w-0 sm:col-span-2 xl:col-span-1">
                      <FormLabel>{t('user_information.tax_id.label')}</FormLabel>
                      <FormControl>
                        <BillWholesaleCustomerField
                          customerName={customerNameWatch ?? ''}
                          field="tax_id"
                          value={field.value ?? ''}
                          onChange={field.onChange}
                          placeholder={t('user_information.tax_id.placeholder')}
                          disabled={isLoading}
                          requiresCustomerHint={t('user_information.autocomplete_requires_customer')}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={control}
                  name="customer_information.brand_mark"
                  render={({ field }) => (
                    <FormItem className="min-w-0 sm:col-span-2 xl:col-span-1">
                      <FormLabel>{t('user_information.brand_mark.label')}</FormLabel>
                      <FormControl>
                        <BillWholesaleCustomerField
                          customerName={customerNameWatch ?? ''}
                          field="brand_mark"
                          value={field.value ?? ''}
                          onChange={field.onChange}
                          placeholder={t('user_information.brand_mark.placeholder')}
                          disabled={isLoading}
                          requiresCustomerHint={t('user_information.autocomplete_requires_customer')}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </>
            ) : null}
          </div>
        </div>
      </FormSection>

      {gridVariant === 'excel' ? (
        <BillFormExcelOrderSection
          control={control}
          lineItemFields={lineItemFields}
          appendLineItem={appendLineItem}
          removeLineItem={removeLineItem}
          lineItems={lineItems}
          isWholesaleSell={isWholesaleSell}
          syncWholesaleRowWeight={syncWholesaleRowWeight}
          disabled={isLoading}
          totalKg={totalKg}
          subtotalBaht={subtotalBaht}
          extraChargeTotal={extraChargeTotal}
          discountWatch={discountWatch}
          isWholesaleSellSummary={isWholesaleSellSummary}
          netBaht={netBaht}
          lineItemsErrorMessage={lineItemsErrorMessage}
          hasLineItemsError={hasLineItemsError}
        />
      ) : (
      <FormSection
        title={t('order_information.title')}
        className="rounded-none border-x border-border/60 bg-transparent"
        contentClassName="space-y-2 p-2 md:p-2"
      >
        <div className="flex flex-col gap-5">
          <BillLineItemsGrid
            control={control}
            fields={lineItemFields}
            append={appendLineItem}
            remove={removeLineItem}
            lineItems={lineItems}
            isWholesaleSell={isWholesaleSell}
            syncWholesaleRowWeight={syncWholesaleRowWeight}
            disabled={isLoading}
            variant={gridVariant}
          />
          <div
            className={cn(
              'rounded-xl border border-primary/20 bg-primary/5 px-4 py-4 md:px-6 md:py-5',
              hasLineItemsError && 'border-destructive/40 bg-destructive/5',
            )}
          >
            <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              สรุปทั้งบิล
            </p>
            <div className="flex flex-col gap-2 text-sm sm:flex-row sm:flex-wrap sm:items-baseline sm:justify-between sm:gap-x-5 sm:gap-y-2">
              <div>
                <span className="text-muted-foreground">น้ำหนักรวม </span>
                <span className="font-bold tabular-nums text-foreground">{formatNumber(totalKg)}</span>
                <span className="text-muted-foreground"> กก.</span>
              </div>
              <div>
                <span className="text-muted-foreground">ยอดทุเรียน </span>
                <span className="font-bold tabular-nums text-foreground">
                  {formatNumber(subtotalBaht)}
                </span>
                <span className="text-muted-foreground"> บาท</span>
              </div>
              {isWholesaleSellSummary ? (
                <div>
                  <span className="text-muted-foreground">ค่าใช้จ่ายเพิ่ม </span>
                  <span className="font-bold tabular-nums text-foreground">
                    {formatNumber(extraChargeTotal)}
                  </span>
                  <span className="text-muted-foreground"> บาท</span>
                </div>
              ) : null}
              {isWholesaleSellSummary ? (
                <div>
                  <span className="text-muted-foreground">หักส่วนลด </span>
                  <span className="font-bold tabular-nums text-foreground">
                    {formatNumber(discountWatch)}
                  </span>
                  <span className="text-muted-foreground"> บาท</span>
                </div>
              ) : null}
              <div className="sm:ml-auto">
                <span className="text-muted-foreground">ยอดสุทธิ (รวมภาษี 0%) </span>
                <span className="font-bold tabular-nums text-foreground">{formatNumber(netBaht)}</span>
                <span className="text-muted-foreground"> บาท</span>
              </div>
            </div>
            {lineItemsErrorMessage ? (
              <p className="mt-3 text-sm font-medium text-destructive">{lineItemsErrorMessage}</p>
            ) : null}
          </div>
        </div>
      </FormSection>
      )}

      <FormSection
        title={t('payment_information.title')}
        className={cn(
          'rounded-b-md border border-border/60 bg-transparent',
          gridVariant === 'excel' ? 'mt-0 rounded-t-md' : 'rounded-t-none',
        )}
        contentClassName="space-y-2 p-2 md:p-2"
      >
        <div className="border border-border/50 bg-transparent p-2">
          <div className="grid min-w-0 gap-3 md:grid-cols-2">
            <FormField
              control={control}
              name="payment_information.payment_method"
              render={({ field }) => (
                <FormItem className="min-w-0">
                  <FormLabel required>{t('payment_information.method.label')}</FormLabel>
                  <FormControl>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger className="h-9 w-full border-border/80 bg-background">
                        <SelectValue placeholder={t('payment_information.method.placeholder')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cash">{t('payment_information.method.cash')}</SelectItem>
                        <SelectItem value="transfer">
                          {t('payment_information.method.transfer')}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="payment_information.payment_status"
              render={({ field }) => (
                <FormItem className="min-w-0">
                  <FormLabel required>{t('payment_information.status.label')}</FormLabel>
                  <FormControl>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger className="h-9 w-full border-border/80 bg-background">
                        <SelectValue placeholder={t('payment_information.status.placeholder')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">
                          {t('payment_information.status.pending')}
                        </SelectItem>
                        <SelectItem value="paid">{t('payment_information.status.paid')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
      </FormSection>
    </>
  )
}
